import React, { useState, useRef, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import { FiXCircle } from 'react-icons/fi';
import { mask } from 'remask';
import { SharedCardDisplayProps } from '../../types';
import QRCodeComponent from '../../../Wallet/QRCode';
import Timer from '../../../Wallet/Timer';
import {
  BeneficiaryCardsSection,
  CardSection,
  CardWrapper,
  Card,
  Front,
  FrontHeader,
  FrontBody,
  FrontTooltip,
  FrontTooltipContent,
  Back,
  BackBody,
  ActuationArea,
  Shortcomings,
  CodANS,
  CodCNS,
  Infos,
  BackBodyFooter,
  BackBodyFooterLeft,
  BackBodyFooterCenter,
  SAC,
  ANSnumber,
  BackTooltip,
  BackTooltipContent,
  TooltipButton,
  SwitchContainer,
  SwitchComponent,
  TabsContainer,
  TabsOptions,
  QRCodeSection,
  QRCodeCard,
} from '../../../Wallet/styles';
import { useOnlineMode } from '../../../../hooks/useOnlineMode';
import { useTheme } from '../../../../hooks/theme';

const mysqlToPTBR = (date: string) => {
  const dateArray = date.split('T')[0].split('-');
  return `${dateArray[2]}/${dateArray[1]}/${dateArray[0]}`;
};

const NO_SHORTCOMING = ['SEM CARÊNCIAS A CUMPRIR', 'SEM CARÊMCIAS A CUMPRIR'];

const SharedCardDisplay: React.FC<SharedCardDisplayProps> = ({
  card,
  activeTab,
  onTabChange,
  onRequestRefetch,
}) => {
  const [flipped, setFlipped] = useState(false);
  const [frontTooltipOpen, setFrontTooltipOpen] = useState(false);
  const [backTooltipOpen, setBackTooltipOpen] = useState(false);
  const [beneficiaryToken, setBeneficiaryToken] = useState(card.cardData);

  const cardWrapperRef = useRef<HTMLDivElement | null>(null);
  const frontRef = useRef<HTMLDivElement | null>(null);
  const backRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();

  const { isOnline } = useOnlineMode();

  const beneficiary = card.cardData;

  useEffect(() => {
    if (beneficiary && activeTab === 'my-card') {
      if (frontRef.current && backRef.current && cardWrapperRef.current) {
        const maxHeight = Math.max(
          frontRef.current.clientHeight,
          backRef.current.clientHeight,
          300,
        );
        cardWrapperRef.current.style.height = `calc(${maxHeight}px + 2.5rem)`;
      }
    }
  }, [beneficiary, activeTab]);

  if (!beneficiary) {
    return (
      <div>
        <p>Carteirinha não disponível</p>
      </div>
    );
  }

  return (
    <>
      <TabsContainer>
        <TabsOptions
          className={activeTab === 'my-card' ? 'active' : ''}
          type="button"
          onClick={() => {
            onTabChange('my-card');
            setFrontTooltipOpen(false);
            setBackTooltipOpen(false);
            setBeneficiaryToken(card.cardData);
          }}
        >
          Meu Cartão
        </TabsOptions>
        <TabsOptions
          className={activeTab === 'qr-code' ? 'active' : ''}
          type="button"
          disabled={!isOnline}
          style={
            !isOnline ? { color: theme.theme.colors.text.grayToHeavyGray } : {}
          }
          onClick={() => {
            onTabChange('qr-code');
            setBeneficiaryToken(card.cardData);
            setFrontTooltipOpen(false);
            setBackTooltipOpen(false);
          }}
        >
          QRCode/Token
        </TabsOptions>
      </TabsContainer>

      <BeneficiaryCardsSection>
        {activeTab === 'my-card' && (
          <CardSection>
            <CardWrapper className={flipped ? 'flip' : ''} ref={cardWrapperRef}>
              <Card>
                <Front ref={frontRef}>
                  <FrontHeader>
                    <div className="images">
                      <img
                        src="/images/logo-unimed-horizontal.png"
                        alt="Unimed Blumenau - Logo"
                        className="logo"
                      />
                    </div>
                    <div className="descriptions">
                      <p>{beneficiary.product}</p>
                      <p>{beneficiary.descriptionTypeOfContract}</p>
                    </div>
                  </FrontHeader>
                  <FrontBody>
                    <div style={{ gridArea: 'usernumber' }}>
                      <h4>
                        {mask(beneficiary.userPlanId, ['9 999 999999999999 9'])}
                      </h4>
                    </div>
                    <div style={{ gridArea: 'username' }}>
                      <p>{beneficiary.beneficiaryName}</p>
                      <small>Nome do Beneficiário</small>
                    </div>
                    <div style={{ gridArea: 'accommodation' }}>
                      <p>{beneficiary.accommodation}</p>
                      <small>Acomodação</small>
                    </div>
                    <div style={{ gridArea: 'validity' }}>
                      <p>{mysqlToPTBR(beneficiary.cardExpirateDate)}</p>
                      <small>Validade</small>
                    </div>
                    <div style={{ gridArea: 'plan' }}>
                      <p>{beneficiary.descRegulation}</p>
                      <small>Plano</small>
                    </div>
                    <div style={{ gridArea: 'attendantNetwork' }}>
                      <p>{beneficiary.serviceNetwork}</p>
                      <small>Rede de Atendimento</small>
                    </div>
                    <div style={{ gridArea: 'scope' }}>
                      <p>{beneficiary.coverage}</p>
                      <small>Abrangência</small>
                    </div>
                    <div style={{ gridArea: 'attendant' }}>
                      <p>{beneficiary.billingLocation}</p>
                      <small>Atend.</small>
                    </div>
                    <div style={{ gridArea: 'assistance' }}>
                      <p>{beneficiary.segmentation}</p>
                      <small>Segmentação Assistencial do Plano</small>
                    </div>
                    <TooltipButton
                      onClick={() => setFrontTooltipOpen((prev) => !prev)}
                    >
                      <FaPlus />
                      <span>Mais informações</span>
                    </TooltipButton>
                  </FrontBody>
                  <FrontTooltip className={frontTooltipOpen ? 'open' : ''}>
                    <button
                      type="button"
                      onClick={() => setFrontTooltipOpen(false)}
                    >
                      <FiXCircle />
                    </button>
                    <FrontTooltipContent>
                      <div>
                        <p>{mysqlToPTBR(beneficiary.birthDate)}</p>
                        <small>Nascimento</small>
                      </div>
                      <div>
                        <p>{beneficiary.validityStartDate}</p>
                        <small>Vigência</small>
                      </div>
                      <div>
                        <p>{beneficiary.cpt ?? 'Não há'}</p>
                        <small>Cob. Parcial Temp.</small>
                      </div>
                      <div>
                        <p>{beneficiary.protocolNumber ?? '-'}</p>
                        <small>Via</small>
                      </div>
                      <div className="fullLine">
                        <p>{beneficiary.contractorName}</p>
                        <small>Contratante</small>
                      </div>
                    </FrontTooltipContent>
                  </FrontTooltip>
                </Front>
                <Back ref={backRef}>
                  <BackBody>
                    <ActuationArea>
                      <p>
                        <strong>Área de Atuação do Produto: </strong>
                        {beneficiary.groupCities.join(', ').toLocaleLowerCase()}
                        .
                      </p>
                    </ActuationArea>
                    <Shortcomings>
                      {beneficiary.descriptionShortcomings.map((item) => (
                        <React.Fragment key={item.shortage}>
                          {NO_SHORTCOMING.includes(item.shortage) ? (
                            <h3>SEM CARÊNCIAS A CUMPRIR</h3>
                          ) : (
                            <div>
                              <p>{item.shortage}</p>
                              <p>{item.validityEndDate}</p>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </Shortcomings>
                    <CodANS>
                      <p>{beneficiary.ansProtocol}</p>
                      <small>Cód. Produto ANS</small>
                    </CodANS>
                    <CodCNS>
                      <p>{beneficiary.cns}</p>
                      <small>CNS</small>
                    </CodCNS>
                    <Infos>
                      Eventuais alterações ocorridas na rede de prestadores
                      poderão ser consultadas no{' '}
                      <a
                        href="www.unimedblumenau.coop.br"
                        target="_blank"
                        rel="noreferrer"
                      >
                        www.unimedblumenau.coop.br
                      </a>{' '}
                      e no telefone{' '}
                      <a
                        href="tel:08006470026"
                        target="_blank"
                        rel="noreferrer"
                      >
                        0800 647-0026
                      </a>
                    </Infos>
                    <BackBodyFooter>
                      {beneficiary.dsInfoSOS && (
                        <BackBodyFooterLeft>
                          <small
                            dangerouslySetInnerHTML={{
                              __html: beneficiary.dsInfoSOS.replace(
                                '\r',
                                '<br />',
                              ),
                            }}
                          />
                        </BackBodyFooterLeft>
                      )}
                      <BackBodyFooterCenter>
                        <SAC>
                          <small>SAC/Informações:</small>
                          <p>0800 647-0026</p>
                        </SAC>
                        <ANSnumber>
                          <div>
                            <p>ANS - nº 33456-1</p>
                          </div>
                        </ANSnumber>
                      </BackBodyFooterCenter>
                    </BackBodyFooter>

                    <TooltipButton
                      onClick={() => setBackTooltipOpen((prev) => !prev)}
                    >
                      <FaPlus />
                      <span>Mais informações</span>
                    </TooltipButton>
                  </BackBody>
                  <BackTooltip className={backTooltipOpen ? 'open' : ''}>
                    <button
                      type="button"
                      onClick={() => setBackTooltipOpen(false)}
                    >
                      <FiXCircle />
                    </button>
                    <BackTooltipContent>
                      <div className="fullLine">
                        <p>{beneficiary.contractedUnimedDescription}</p>
                        <small>Unimed contratante</small>
                      </div>
                      <div className="fullLine">
                        <p>334561</p>
                        <small>Nº registro ANS</small>
                      </div>
                    </BackTooltipContent>
                  </BackTooltip>
                </Back>
              </Card>
            </CardWrapper>
            <SwitchContainer>
              <span>FRENTE</span>
              <SwitchComponent
                onChange={() => {
                  setFlipped((prev) => !prev);
                  setFrontTooltipOpen(false);
                  setBackTooltipOpen(false);
                }}
                checked={flipped}
                checkedIcon={false}
                uncheckedIcon={false}
                height={20}
                width={40}
                handleDiameter={20}
                offColor="#c2c2c2"
                onColor="#c2c2c2"
              />
              <span>VERSO</span>
            </SwitchContainer>
          </CardSection>
        )}

        {activeTab === 'qr-code' && (
          <QRCodeSection>
            <QRCodeCard>
              {beneficiaryToken && card.qrCodeData ? (
                <>
                  <h3>{card.qrCodeData.ds_trilha_3}</h3>
                  <div className="qrCode">
                    <QRCodeComponent
                      code={card.qrCodeData.descTrailQrCode}
                      key={card.qrCodeData.ds_trilha_3}
                    />
                  </div>
                  <Timer
                    min={2}
                    onExpire={onRequestRefetch}
                    key={card.qrCodeData.ds_trilha_3}
                  />
                </>
              ) : (
                <h3>QRCode não disponível</h3>
              )}
            </QRCodeCard>
          </QRCodeSection>
        )}
      </BeneficiaryCardsSection>
    </>
  );
};

export default SharedCardDisplay;
